import { GlassCard } from '@/components/design-system';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ListingsResponse } from '@/lib/collections/listings';
import { PLASTIC_TYPE_LABELS, formatCentsAsUsd, isPlasticType } from '@/utils/plastic-rates';
import { CalendarClock, Check, Copy, ExternalLink, Package } from 'lucide-react';
import { toast } from 'sonner';

function shortSig(sig: string): string {
  return sig.length > 8 ? `${sig.slice(0, 4)}…${sig.slice(-4)}` : sig;
}

function formatPickupTime(scheduledFor: number): string {
  const date = new Date(scheduledFor * 1000);
  const day = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `Pickup: ${day} · ${time}`;
}

const STATUS_STYLES: Record<string, string> = {
  open: 'border-primary/40 bg-primary/10 text-primary',
  accepted: 'border-accent/40 bg-accent/10 text-accent',
  scheduled: 'border-[hsl(var(--amber))]/40 bg-[hsl(var(--amber))]/10 text-[hsl(var(--amber))]',
  completed: 'border-border/60 bg-secondary/40 text-muted-foreground',
};

const STAGES = [
  { key: 'open', label: 'Open' },
  { key: 'accepted', label: 'Accepted' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'completed', label: 'Completed' },
] as const;

interface ListingTimelineCardProps {
  listing: ListingsResponse;
}

/**
 * One recovery listing card for the household's own tracker: photo, plastic
 * type, weight, current status badge, plus a 4-stage progress stepper
 * (open -> accepted -> scheduled -> completed) with the current stage lit up.
 */
export function ListingTimelineCard({ listing }: ListingTimelineCardProps) {
  const rawIndex = STAGES.findIndex((s) => s.key === listing.status);
  const currentIndex = rawIndex === -1 ? 0 : rawIndex;

  const copySig = async () => {
    if (!listing.txSignature) return;
    try {
      await navigator.clipboard.writeText(listing.txSignature);
      toast.success('Transaction signature copied');
    } catch {
      toast.error('Could not copy.');
    }
  };

  return (
    <GlassCard padding="sm" className="h-full">
      <div className="flex gap-3">
        <div className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-secondary/50">
          {listing.photoUrl ? (
            <img src={listing.photoUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Package className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-foreground">
              {isPlasticType(listing.plasticType) ? PLASTIC_TYPE_LABELS[listing.plasticType] : listing.plasticType}
            </span>
            <Badge variant="outline" className={STATUS_STYLES[listing.status] ?? STATUS_STYLES.open}>
              {listing.status}
            </Badge>
          </div>
          <div className="mt-2 flex items-baseline gap-3">
            <span className="text-sm font-medium text-foreground">{listing.estimatedKg} kg</span>
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-sm font-bold text-transparent">
              {formatCentsAsUsd(listing.estimatedValue)}
            </span>
          </div>
        </div>
      </div>

      {/* Progress stepper */}
      <div className="mt-4 border-t border-border/60 pt-4">
        <div className="flex items-start">
          {STAGES.map((stage, i) => {
            const done = i < currentIndex;
            const active = i === currentIndex;
            return (
              <div key={stage.key} className={cn('flex items-center', i < STAGES.length - 1 && 'flex-1')}>
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={cn(
                      'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold transition-all duration-300',
                      done && 'border-primary bg-primary text-primary-foreground',
                      active && !done && 'border-primary bg-primary/15 text-primary ring-2 ring-primary/30',
                      !done && !active && 'border-border/60 bg-secondary/30 text-muted-foreground'
                    )}
                  >
                    {done ? <Check className="h-3 w-3" strokeWidth={2.5} /> : i + 1}
                  </div>
                  <span
                    className={cn(
                      'whitespace-nowrap text-[10px] font-medium',
                      active ? 'text-foreground' : 'text-muted-foreground'
                    )}
                  >
                    {stage.label}
                  </span>
                </div>
                {i < STAGES.length - 1 && (
                  <div
                    className={cn(
                      'mx-1 h-px flex-1 -translate-y-2.5',
                      i < currentIndex ? 'bg-primary' : 'bg-border/60'
                    )}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Scheduled — pickup date & time */}
      {listing.status === 'scheduled' && listing.scheduledFor && (
        <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
          <CalendarClock className="h-3.5 w-3.5 text-[hsl(var(--amber))]" />
          {formatPickupTime(listing.scheduledFor)}
        </p>
      )}

      {/* Completed — payment settled onchain */}
      {listing.status === 'completed' && listing.txSignature && (
        <div className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-secondary/30 px-2.5 py-2">
          <span className="text-xs font-semibold text-foreground">
            {formatCentsAsUsd(listing.estimatedValue)} USDC paid
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={copySig}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground"
              title="Copy signature"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
            <a
              href={`https://solscan.io/tx/${listing.txSignature}`}
              target="_blank"
              rel="noopener noreferrer"
              className={cn('flex items-center gap-1 rounded-md p-1 text-muted-foreground transition-colors hover:text-foreground')}
              title="View on Solscan"
            >
              <span className="font-mono text-[11px]">{shortSig(listing.txSignature)}</span>
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      )}
    </GlassCard>
  );
}

export default ListingTimelineCard;
